import { useEffect, useState } from "react";
import PageSeo from "@/components/seo/PageSeo";
import SeoImage from "@/components/seo/SeoImage";
import { useParams, Link, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import { asArray } from "@/lib/arrays";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import {
  ArrowRight, ArrowLeft, BookOpen, CheckCircle, Check, Clock, Users, Award,
  PlayCircle, Star, Zap, ChevronDown, ChevronUp, Menu, X, GraduationCap,
  Target, Shield, FileText, Video, Monitor, CreditCard, Quote, Brain,
  Calculator, Medal, MapPin, Phone, Mail, Sparkles, TrendingUp, Activity
} from "lucide-react";
import { toast } from "sonner";
import Footer from "@/components/layout/Footer";
import { startRazorpayCourseCheckout } from "@/lib/payment";
import TradingHorizonHeroBg from "@/components/hero/TradingHorizonHeroBg";

const TELEGRAM_LINK = "https://t.me/+QOePMys7V481NmZl";
const MENTOR_PHOTO = "/deepak-sir.webp";

const Reveal = ({ children, className = "", delay = 0 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-60px" });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 24 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5, delay, ease: [0.22, 1, 0.36, 1] }} className={className}>
      {children}
    </motion.div>
  );
};

const CourseDetailPage = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { login, user } = useAuth();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedModules, setExpandedModules] = useState({});
  const [allCourses, setAllCourses] = useState([]);
  const [enrolling, setEnrolling] = useState(false);
  const [activeFaq, setActiveFaq] = useState(null);
  const [showEnrollModal, setShowEnrollModal] = useState(false);
  const [enrollType, setEnrollType] = useState("paid");
  const [payingOnline, setPayingOnline] = useState(false);
  const [checkoutRefCode, setCheckoutRefCode] = useState(() => {
    try {
      return (
        sessionStorage.getItem("jamadhan_referral_code") ||
        localStorage.getItem("jamadhan_referral_code") ||
        ""
      ).trim().toUpperCase();
    } catch (_) {
      return "";
    }
  });
  const [selectedCampusImage, setSelectedCampusImage] = useState("/jamadhan_reception.webp");
  const [mobileMenu, setMobileMenu] = useState(false);

  useEffect(() => {
    const fetch = async () => {
      try {
        let detailRes = null;

        // Try 1: /courses/detail/<id>
        try {
          detailRes = await axios.get(`${API}/courses/detail/${courseId}`);
        } catch (e1) { /* continue */ }

        // Try 2: /courses/by-slug/<slug>
        if (!detailRes) {
          try {
            detailRes = await axios.get(`${API}/courses/by-slug/${courseId}`);
          } catch (e2) { /* continue */ }
        }

        // Try 3: /courses/<id> (direct)
        if (!detailRes) {
          try {
            detailRes = await axios.get(`${API}/courses/${courseId}`);
          } catch (e3) { /* continue */ }
        }

        if (!detailRes) throw new Error("Course not found");

        const coursesRes = await axios.get(`${API}/courses`);
        setCourse(detailRes.data);
        setAllCourses(asArray(coursesRes.data, "courses").filter(c => c.course_id !== detailRes.data.course_id));
      } catch (err) {
        console.error("Error fetching course detail:", err);
        toast.error(err.response?.data?.detail || err.message || "Failed to fetch course detail");
      } finally {
        setLoading(false);
      }
    };
    fetch();
    window.scrollTo(0, 0);
  }, [courseId]);

  const LEGACY_SLUG_REDIRECTS = {
    '1': '/courses/smart-trader-elementary',
    'super-trader-edge': '/courses/technical-analysis-course',
    'smart-edge': '/courses/technical-analysis-course',
    'super-option-analysis': '/courses/options-trading-course',
    'super-options-analysis': '/courses/options-trading-course',
    'super-trader-combo': '/courses/technical-analysis-options-combo-course',
  };

  const targetRedirect = LEGACY_SLUG_REDIRECTS[courseId?.toLowerCase()?.trim()];
  if (targetRedirect) {
    return <Navigate to={targetRedirect} replace />;
  }

  const toggleModule = (id) => setExpandedModules(prev => ({ ...prev, [id]: !prev[id] }));
  const meta = course?.meta || {};

  const handleEnroll = async () => {
    if (!user) {
      toast.info("Please sign in or register to enroll in this course.");
      navigate(`/login?redirect=${encodeURIComponent(location.pathname)}`);
      return;
    }
    setShowEnrollModal(true);
  };

  const submitEnrollmentRequest = async () => {
    setEnrolling(true);
    try {
      await axios.post(`${API}/enrollments/request`, {
        course_id: course.course_id,
        request_type: enrollType
      }, { withCredentials: true });
      toast.success("Request sent to admin successfully!");
      setShowEnrollModal(false);
    } catch (err) {
      if (err.response && err.response.data && err.response.data.detail) {
        toast.error(err.response.data.detail);
      } else {
        toast.error("Failed to send request. Please try again.");
      }
    } finally {
      setEnrolling(false);
    }
  };

  const handleOnlineCoursePayment = async () => {
    if (!user) {
      toast.info("Please sign in to proceed with online course checkout.");
      navigate(`/login?redirect=${encodeURIComponent(location.pathname)}`);
      return;
    }
    if (!course) return;
    setPayingOnline(true);
    await startRazorpayCourseCheckout({
      courseId: course.course_id,
      user,
      referralCode: checkoutRefCode.trim().toUpperCase() || undefined,
      onSuccess: () => {
        setPayingOnline(false);
        setShowEnrollModal(false);
        setIsEnrolled(true);
      },
      onError: () => {
        setPayingOnline(false);
      }
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-[#00FF88] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!course) {
    return (
      <div className="min-h-screen bg-[#050505] flex flex-col items-center justify-center gap-4">
        <p className="text-[#CBD5E1]">Course not found for ID: {courseId}</p>
        <Link to="/"><Button className="bg-[#00FF88] text-black font-bold"><ArrowLeft className="w-4 h-4 mr-2" /> Back Home</Button></Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="course-detail-page">
      <PageSeo path={`/courses/${course?.slug || courseId}/`} fallbackTitle={course?.title || "Course"} fallbackDescription={course?.description || ""} />
      {/* ═══ HEADER ═══ */}
      <header className="sticky top-0 z-50 bg-[#050505]/90 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5 group">
            <div className="w-9 h-9 rounded-xl overflow-hidden shadow-[0_0_20px_rgba(0,255,136,0.25)] border border-[#00FF88]/30 group-hover:border-[#00FF88] transition-colors p-0.5 bg-black">
              <img src="/jamadhan-logo.png" alt="Jamadhan Logo" className="w-full h-full object-contain" />
            </div>
            <div className="flex flex-col">
              <span className="text-base sm:text-lg font-black text-white font-outfit tracking-tight group-hover:text-[#00FF88] transition-colors">
                JAMADHAN
              </span>
              <span className="text-[9px] font-mono tracking-widest text-[#00F0FF] uppercase">Stock Market Institute</span>
            </div>
          </Link>

          {/* Desktop Navigation Links */}
          <nav className="hidden md:flex items-center gap-6 text-sm font-medium font-outfit text-zinc-300">
            <Link to="/" className="hover:text-[#00FF88] transition-colors">Home</Link>
            <Link to="/about/" className="hover:text-[#00FF88] transition-colors">About</Link>
            <Link to="/#courses" className="hover:text-[#00FF88] transition-colors">Courses</Link>
            <Link to="/gallery/" className="hover:text-[#00FF88] transition-colors">Gallery</Link>
            <Link to="/drishti-ai/" className="hover:text-[#00FF88] transition-colors text-[#00F0FF] font-semibold flex items-center gap-1">
              <Sparkles className="w-3.5 h-3.5" /> Drishti AI
            </Link>
            <Link to="/contact/" className="hover:text-[#00FF88] transition-colors">Contact</Link>
          </nav>

          {/* Header Action Buttons */}
          <div className="hidden sm:flex items-center gap-3">
            <Button
              onClick={handleEnroll}
              size="sm"
              className="bg-[#00FF88] text-black font-bold hover:bg-black hover:text-white border border-transparent hover:border-[#00FF88] rounded-sm px-4 h-9 transition-all duration-200"
              data-testid="enroll-header-btn"
            >
              {enrolling ? "Processing..." : (course.is_free ? "Start Free" : "Enroll Now")}
            </Button>
            <Link
              to={user ? "/dashboard" : "/login"}
              className="px-4 py-1.5 rounded-sm bg-white/[0.06] hover:bg-white/[0.12] text-white font-bold text-xs font-outfit transition-all border border-white/10 h-9 flex items-center"
            >
              {user ? "Dashboard" : "Login"}
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenu(!mobileMenu)}
            className="md:hidden text-white p-2"
            aria-label="Toggle navigation menu"
          >
            {mobileMenu ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Dropdown Menu */}
        {mobileMenu && (
          <div className="md:hidden bg-[#050505]/98 border-t border-white/5 text-white backdrop-blur-xl px-6 py-4 space-y-3">
            <Link to="/" onClick={() => setMobileMenu(false)} className="block py-2 text-[#CBD5E1] hover:text-[#00FF88]">Home</Link>
            <Link to="/about/" onClick={() => setMobileMenu(false)} className="block py-2 text-[#CBD5E1] hover:text-[#00FF88]">About</Link>
            <Link to="/#courses" onClick={() => setMobileMenu(false)} className="block py-2 text-[#CBD5E1] hover:text-[#00FF88]">Courses</Link>
            <Link to="/gallery/" onClick={() => setMobileMenu(false)} className="block py-2 text-[#CBD5E1] hover:text-[#00FF88]">Gallery</Link>
            <Link to="/drishti-ai/" onClick={() => setMobileMenu(false)} className="block py-2 text-[#00F0FF] hover:text-[#00FF88] font-semibold">Drishti AI</Link>
            <Link to="/contact/" onClick={() => setMobileMenu(false)} className="block py-2 text-[#CBD5E1] hover:text-[#00FF88]">Contact</Link>
            <div className="pt-2 flex flex-col gap-2">
              <Button onClick={() => { setMobileMenu(false); handleEnroll(); }} className="w-full bg-[#00FF88] text-black font-bold">
                {enrolling ? "Processing..." : (course.is_free ? "Start Free" : "Enroll Now")}
              </Button>
              <Link to={user ? "/dashboard" : "/login"} onClick={() => setMobileMenu(false)} className="w-full py-2 text-center rounded bg-white/10 text-white font-bold text-sm">
                {user ? "Dashboard" : "Login"}
              </Link>
            </div>
          </div>
        )}
      </header>

      {/* ═══ HERO WITH TRADINGVIEW-GRADE EARTH HORIZON & CANDLESTICKS ═══ */}
      <TradingHorizonHeroBg className="pt-10 pb-16 md:pb-24 border-b border-white/5" showBeams={true}>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            
            {/* Left Column: Course Headline, Price & Action */}
            <div className="lg:col-span-7">
              <Reveal>
                <div className="flex flex-wrap items-center gap-2 mb-4">
                  {course.is_free ? (
                    <Badge className="bg-[#00FF88]/20 text-[#00FF88] border border-[#00FF88]/40 rounded-sm font-jetbrains text-xs tracking-wider px-2.5 py-1">
                      FREE INTRODUCTORY COURSE
                    </Badge>
                  ) : (
                    <Badge className="bg-[#00F0FF]/15 text-[#00F0FF] border border-[#00F0FF]/35 rounded-sm font-jetbrains text-xs tracking-wider px-2.5 py-1">
                      {course.sessions ? `${course.sessions} LIVE SESSIONS` : "LIVE MENTORSHIP"}
                    </Badge>
                  )}
                  <Badge className="bg-white/10 text-white border border-white/15 rounded-sm text-xs font-jetbrains px-2.5 py-1 backdrop-blur-md">
                    {course.duration || "Interactive Live Curriculum"}
                  </Badge>
                  <span className="inline-flex items-center gap-1.5 text-[11px] font-mono text-[#00FF88] bg-black/40 px-2 py-0.5 rounded border border-[#00FF88]/20">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#00FF88] animate-ping" />
                    LIVE ON ZOOM & JAIPUR CAMPUS
                  </span>
                </div>

                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white font-outfit tracking-tight leading-[1.08] mb-4 drop-shadow-2xl">
                  {course.title}
                </h1>

                {meta.tagline && (
                  <p className="text-lg sm:text-xl text-[#00F0FF] font-outfit font-medium mb-4 drop-shadow-md">
                    {meta.tagline}
                  </p>
                )}

                <p className="text-sm sm:text-base text-[#CBD5E1] font-plex mb-8 leading-relaxed max-w-2xl">
                  {course.description}
                </p>

                {/* Price + CTA */}
                <div className="flex flex-wrap items-center gap-4 mb-8">
                  <div className="flex flex-col">
                    <span className="text-[10px] font-mono uppercase tracking-wider text-zinc-400">Total Course Fee</span>
                    <span className={`text-3xl sm:text-4xl font-black font-jetbrains tracking-tight ${course.is_free ? 'text-[#00FF88]' : 'text-white'}`}>
                      {course.price_display}
                    </span>
                  </div>

                  <Button 
                    onClick={handleEnroll} 
                    size="lg" 
                    className="bg-[#00FF88] text-black font-extrabold hover:bg-white hover:text-black border border-transparent hover:shadow-[0_0_30px_rgba(0,255,136,0.5)] rounded-sm px-8 h-12 text-sm sm:text-base transition-all duration-200 shadow-xl" 
                    data-testid="enroll-hero-btn"
                  >
                    {enrolling ? "Processing..." : (course.is_free ? "Start Free Course" : "Enroll Now")} <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </div>

                {/* 6 Key Highlights Grid */}
                {meta.highlights && (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5 sm:gap-3 max-w-xl">
                    {meta.highlights.map((h, i) => {
                      const isDrishti = h.label?.toLowerCase().includes("drishti");
                      const displayValue = isDrishti ? "3 Months Free" : h.value;
                      return (
                        <div 
                          key={h.label} 
                          className="rounded-lg p-3 text-left bg-black/40 backdrop-blur-md border border-white/[0.08] hover:border-[#00FF88]/40 transition-all shadow-lg" 
                          data-testid={`highlight-${i}`}
                        >
                          <p className="text-[10px] text-[#A1A1AA] font-jetbrains uppercase tracking-wider mb-0.5">{h.label}</p>
                          <p className={`text-xs sm:text-sm font-bold font-outfit ${isDrishti ? 'text-[#00FF88]' : 'text-white'}`}>
                            {displayValue}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                )}
              </Reveal>
            </div>

            {/* Right Column: Institutional Terminal Card (Desktop Showcase) */}
            <div className="hidden lg:block lg:col-span-5">
              <Reveal delay={0.2}>
                <div className="relative rounded-2xl border border-white/10 bg-gradient-to-b from-[#0A0E17]/90 to-[#05070E]/95 backdrop-blur-2xl p-6 shadow-[0_20px_60px_rgba(0,0,0,0.8)] overflow-hidden">
                  
                  {/* Subtle Top Glowing Line */}
                  <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-[#00FF88] to-transparent" />
                  
                  {/* Terminal Header */}
                  <div className="flex items-center justify-between pb-4 border-b border-white/[0.08] mb-5">
                    <div className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full bg-[#00FF88] animate-pulse" />
                      <span className="text-xs font-mono font-bold tracking-widest uppercase text-white">
                        INSTITUTIONAL DESK
                      </span>
                    </div>
                    <span className="text-[10px] font-mono bg-[#00FF88]/10 text-[#00FF88] px-2 py-0.5 rounded border border-[#00FF88]/25 font-bold">
                      ISO 21001:2018
                    </span>
                  </div>

                  {/* Active Market Radar */}
                  <div className="p-3.5 rounded-xl bg-white/[0.02] border border-white/[0.06] mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[11px] font-mono font-bold text-zinc-300">DEMAND-SUPPLY EXECUTION</span>
                      <span className="text-[10px] font-mono text-[#00FF88] bg-[#00FF88]/10 px-2 py-0.5 rounded font-bold">ACCURACY 85%</span>
                    </div>
                    <div className="flex items-baseline gap-2">
                      <span className="text-xl font-black font-jetbrains text-white">NIFTY 50</span>
                      <span className="text-xs font-mono text-[#00FF88] font-bold">INSTITUTIONAL SETUP READY</span>
                    </div>
                    <p className="text-[11px] text-[#94A3B8] font-plex mt-1.5 leading-relaxed">
                      Learn to trade where financial institutions place multimillion-dollar orders before retail traders enter.
                    </p>
                  </div>

                  {/* Program Features List */}
                  <div className="space-y-2.5 mb-5">
                    {[
                      { icon: Activity, title: "Live Market Execution Room", desc: "Live chart practice with Deepak Sir during NSE trading hours" },
                      { icon: Zap, title: "Drishti AI Scanner (3 Mo Free)", desc: "Algorithmic scanner screening 500+ NSE stocks for institutional zones" },
                      { icon: Shield, title: "Risk Management & Greeks", desc: "Position sizing, capital preservation & institutional hedging" },
                      { icon: Award, title: "Govt & ISO Certified", desc: "Authorized institutional completion certificate upon graduation" }
                    ].map((f, idx) => (
                      <div key={idx} className="flex items-start gap-2.5 text-xs text-zinc-300">
                        <div className="w-5 h-5 rounded bg-[#00FF88]/10 border border-[#00FF88]/30 flex items-center justify-center shrink-0 mt-0.5">
                          <Check className="w-3.5 h-3.5 text-[#00FF88] stroke-[3]" />
                        </div>
                        <div>
                          <span className="font-bold text-white block">{f.title}</span>
                          <span className="text-[11px] text-zinc-400">{f.desc}</span>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Batch Alert Banner */}
                  <div className="pt-3 border-t border-white/[0.08] flex items-center justify-between">
                    <div>
                      <span className="text-[10px] font-mono text-zinc-400 block uppercase">Upcoming Batch</span>
                      <span className="text-xs font-bold text-white">Live Seats Forming</span>
                    </div>
                    <Button 
                      onClick={handleEnroll} 
                      size="sm" 
                      className="bg-white/10 hover:bg-[#00FF88] hover:text-black text-white text-xs font-bold h-8 px-4 rounded border border-white/15 transition-all"
                    >
                      Check Availability →
                    </Button>
                  </div>

                </div>
              </Reveal>
            </div>

          </div>
        </div>
      </TradingHorizonHeroBg>

      {/* ═══ WHAT YOU'LL LEARN ═══ */}
      {meta.what_you_learn && (
        <section className="py-16 px-6 relative" data-testid="what-you-learn">
          <div className="absolute inset-0 bg-market opacity-50" />
          <div className="max-w-5xl mx-auto relative z-10">
            <Reveal>
              <div className="terminal-card rounded-lg p-8">
                <p className="font-jetbrains text-xs tracking-[0.25em] uppercase text-[#00FF88] mb-3">CURRICULUM</p>
                <h2 className="text-3xl sm:text-4xl font-bold text-white font-outfit mb-8">What You'll Learn</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {meta.what_you_learn.map((item, i) => (
                    <Reveal key={`learn-${i}-${item.slice(0,10)}`} delay={i * 0.04}>
                      <div className="flex items-start gap-3 group" data-testid={`learn-item-${i}`}>
                        <div className="w-6 h-6 rounded bg-[#00FF88]/10 flex items-center justify-center shrink-0 mt-0.5 group-hover:bg-[#00FF88]/20 transition-colors">
                          <CheckCircle className="w-3.5 h-3.5 text-[#00FF88]" />
                        </div>
                        <p className="text-base text-[#CBD5E1] font-plex leading-relaxed">{item}</p>
                      </div>
                    </Reveal>
                  ))}
                </div>
              </div>
            </Reveal>
          </div>
        </section>
      )}

      {/* ═══ COURSE MODULES / SYLLABUS ═══ */}
      {course.modules_data?.length > 0 && (
        <section className="py-16 px-6" data-testid="course-modules">
          <div className="max-w-5xl mx-auto">
            <Reveal>
              <p className="font-jetbrains text-xs tracking-[0.25em] uppercase text-[#00F0FF] mb-3">SESSION-WISE BREAKDOWN</p>
              <h2 className="text-2xl sm:text-3xl font-bold text-white font-outfit mb-8">Course Modules</h2>
            </Reveal>

            <div className="space-y-3">
              {course.modules_data.map((mod, i) => (
                <Reveal key={mod.module_id} delay={i * 0.05}>
                  <div className="terminal-card rounded-lg overflow-hidden" data-testid={`module-${i}`}>
                    <button
                      onClick={() => toggleModule(mod.module_id)}
                      className="w-full flex items-center gap-4 p-4 hover:bg-white/[0.02] transition-colors text-left"
                    >
                      <div className="w-10 h-10 rounded bg-gradient-to-br from-[#00FF88]/10 to-[#00F0FF]/10 flex items-center justify-center shrink-0">
                        <span className="font-jetbrains text-sm font-bold text-[#00FF88]">{String(i + 1).padStart(2, '0')}</span>
                      </div>
                      <div className="flex-1">
                        <h3 className="text-base font-semibold text-white font-outfit">{mod.title}</h3>
                        <p className="text-xs text-[#94A3B8] font-plex mt-0.5">
                          {mod.lessons?.length || 0} lessons
                        </p>
                      </div>
                      {expandedModules[mod.module_id] ? (
                        <ChevronUp className="w-4 h-4 text-[#94A3B8]" />
                      ) : (
                        <ChevronDown className="w-4 h-4 text-[#94A3B8]" />
                      )}
                    </button>

                    {expandedModules[mod.module_id] && mod.lessons?.length > 0 && (
                      <div className="border-t border-white/5">
                        {mod.lessons.map((lesson, li) => (
                          <div key={li} className="flex items-center gap-3 px-4 py-3 border-b border-white/[0.03] last:border-0">
                            <PlayCircle className="w-4 h-4 text-[#94A3B8] shrink-0" />
                            <span className="text-sm text-[#CBD5E1] font-plex flex-1">{lesson.title}</span>
                            <span className="text-xs text-[#94A3B8] font-jetbrains">
                              {lesson.duration_minutes ? `${lesson.duration_minutes}m` : "Live"}
                            </span>
                            {lesson.is_preview && (
                              <Badge className="bg-[#00FF88]/10 text-[#00FF88] border-0 text-[10px] rounded-sm">FREE</Badge>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ═══ WHO IS THIS FOR ═══ */}
      {meta.who_is_for && (
        <section className="py-16 px-6 relative" data-testid="who-is-for">
          <div className="absolute inset-0 grid-terminal opacity-30" />
          <div className="max-w-5xl mx-auto relative z-10">
            <Reveal>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
                <div>
                  <p className="font-jetbrains text-xs tracking-[0.25em] uppercase text-[#FFCC00] mb-3">FOR YOU</p>
                  <h2 className="text-3xl sm:text-4xl font-bold text-white font-outfit mb-6">Who This Course Is For</h2>
                  <div className="space-y-4">
                    {meta.who_is_for.map((item, i) => (
                      <div key={`who-${i}-${item.slice(0,15)}`} className="flex items-start gap-3" data-testid={`who-item-${i}`}>
                        <Target className="w-5 h-5 text-[#FFCC00] shrink-0 mt-0.5" />
                        <p className="text-base text-[#CBD5E1] font-plex">{item}</p>
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="font-jetbrains text-xs tracking-[0.25em] uppercase text-[#00FF88] mb-3">RESULTS</p>
                  <h2 className="text-3xl sm:text-4xl font-bold text-white font-outfit mb-6">Outcomes</h2>
                  <div className="space-y-4">
                    {(meta.outcomes || []).map((item, i) => (
                      <div key={`outcome-${i}-${item.slice(0,15)}`} className="flex items-start gap-3" data-testid={`outcome-item-${i}`}>
                        <Award className="w-5 h-5 text-[#00FF88] shrink-0 mt-0.5" />
                        <p className="text-base text-[#CBD5E1] font-plex">{item}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </section>
      )}

      {/* ═══ INSTRUCTOR ═══ */}
      <section className="py-16 px-6 relative overflow-hidden" data-testid="instructor-section">
        <div className="absolute inset-0 grid-terminal opacity-30" />
        <div className="absolute left-0 top-1/3 h-72 w-72 rounded-full bg-[#00FF88]/8 blur-3xl" />
        <div className="max-w-6xl mx-auto relative z-10">
          <Reveal>
            <div className="terminal-card overflow-hidden rounded-2xl border border-[#00FF88]/20 bg-[#090C10] shadow-[0_20px_50px_rgba(0,0,0,0.6)]">
              {/* Main Content Area */}
              <div className="p-6 sm:p-8 lg:p-10">
                {/* Top Section: Passport Photo + Bio */}
                <div className="flex flex-col md:flex-row items-center md:items-start gap-6 lg:gap-8">
                  
                  {/* Passport-sized Photo Card */}
                  <div className="shrink-0 flex flex-col items-center text-center">
                    <div className="relative w-40 sm:w-48 aspect-[3.5/4.5] rounded-2xl overflow-hidden border-2 border-[#00FF88]/30 bg-[#06080B] shadow-[0_8px_30px_rgba(0,255,136,0.15)] group">
                      <img
                        src={MENTOR_PHOTO}
                        alt="Deepak Sethia"
                        className="w-full h-full object-cover object-[center_18%] group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-transparent to-transparent" />
                      <div className="absolute top-2.5 left-2.5 rounded-full border border-[#00FF88]/30 bg-black/60 px-2.5 py-0.5 backdrop-blur">
                        <p className="font-jetbrains text-[9px] uppercase tracking-wider text-[#00FF88]">Verified</p>
                      </div>
                      <div className="absolute bottom-2.5 left-2 right-2 text-center">
                        <span className="inline-block text-[9px] font-bold font-jetbrains tracking-wider uppercase text-black bg-[#00FF88] px-2 py-0.5 rounded shadow-sm">
                          Founder & Mentor
                        </span>
                      </div>
                    </div>
                    <p className="font-outfit text-lg font-bold text-white mt-3">Deepak Sethia</p>
                    <p className="text-xs text-[#00F0FF] font-medium">Founder, JamaDhan Institute</p>
                  </div>

                  {/* Headline, Story & Stats */}
                  <div className="flex-1 min-w-0 text-center md:text-left">
                    <p className="font-jetbrains text-xs uppercase tracking-[0.28em] text-[#00F0FF]">Know Your Instructor</p>
                    <h2 className="mt-2 font-outfit text-2xl sm:text-3xl lg:text-4xl font-black leading-tight text-white">
                      Still losing money on tips, noise, and random entries?
                    </h2>
                    <p className="mt-4 text-sm sm:text-base leading-relaxed text-[#CBD5E1]">
                      Deepak Sethia has spent over 12+ years in the stock market and has trained more than 10,000 students to stop depending on tips and start reading the market with structure, calculated math, and discipline.
                    </p>
                    <p className="mt-3 text-sm sm:text-base leading-relaxed text-[#CBD5E1]">
                      His teaching style is built for real transformation: students learn how to identify quality setups, control risk, manage trades, and build confidence through a practical system instead of emotional guessing.
                    </p>

                    {/* 3 Stats Grid */}
                    <div className="mt-6 grid grid-cols-3 gap-3">
                      {[
                        { value: "12+", label: "Years Market Experience" },
                        { value: "10000+", label: "Students Trained" },
                        { value: "90+", label: "Success Rate" },
                      ].map((stat) => (
                        <div key={stat.label} className="rounded-xl border border-white/[0.08] bg-white/[0.03] p-3 text-center md:text-left hover:border-[#00FF88]/30 transition-colors">
                          <p className="font-jetbrains text-xl font-bold text-white sm:text-2xl">{stat.value}</p>
                          <p className="mt-1 text-[10px] uppercase tracking-[0.14em] text-[#94A3B8]">{stat.label}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Divider */}
                <div className="my-8 border-t border-white/[0.06]" />

                {/* Quote Card */}
                <div className="rounded-xl border border-[#00FF88]/20 bg-[#00FF88]/[0.04] p-5 sm:p-6 mb-8">
                  <Quote className="mb-2 h-6 w-6 text-[#00FF88]" />
                  <p className="font-plex text-sm sm:text-base italic leading-relaxed text-white">
                    "A trader does not become profitable by chasing tips. Profitability starts when you understand price, manage risk, and follow a repeatable system with patience."
                  </p>
                </div>

                {/* 4 Methodology Pillars */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  {[
                    { icon: Calculator, title: "Calculated Trading", text: "Decisions based on math, levels, and risk, not emotion." },
                    { icon: Brain, title: "Clear Market Logic", text: "Understand why price moves instead of copying calls." },
                    { icon: Shield, title: "Risk First Method", text: "Protect capital with entry, stop loss, and trade sizing." },
                    { icon: Medal, title: "Real Mentor Support", text: "Learn from a mentor who has trained 10,000+ traders." },
                  ].map((item) => (
                    <div key={item.title} className="group rounded-xl border border-white/[0.06] bg-white/[0.025] p-4 transition-all hover:-translate-y-1 hover:border-[#00FF88]/25">
                      <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-lg bg-[#00FF88]/10 shadow-[0_0_20px_rgba(0,255,136,0.10)]">
                        <item.icon className="h-5 w-5 text-[#00FF88]" strokeWidth={1.5} />
                      </div>
                      <h3 className="font-outfit text-base font-bold text-white">{item.title}</h3>
                      <p className="mt-1 text-xs leading-5 text-[#94A3B8]">{item.text}</p>
                    </div>
                  ))}
                </div>

                {/* CTA Button */}
                <div className="mt-8 flex justify-center md:justify-start">
                  <Button onClick={handleEnroll} className="bg-[#00FF88] text-black font-bold hover:bg-black hover:text-white border border-transparent hover:border-[#00FF88] rounded-sm px-8 py-3 h-auto text-sm transition-all shadow-[0_0_20px_rgba(0,255,136,0.2)]">
                    Learn With Deepak Sir <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ═══ ENROLL CTA ═══ */}
      <section className="py-16 px-6" data-testid="enroll-cta">
        <Reveal>
          <div className="max-w-3xl mx-auto text-center">
            <div className="rounded-lg p-8 md:p-12 border border-[#00FF88]/20 bg-[#00FF88]/[0.03]" style={{ animation: 'glow-pulse 3s ease-in-out infinite' }}>
              <h2 className="text-3xl sm:text-4xl font-bold text-white font-outfit mb-3">
                Ready to {course.is_free ? "Start Learning" : "Level Up"}?
              </h2>
              <p className="text-[#CBD5E1] font-plex mb-6 text-lg">
                {course.is_free
                  ? "Begin your trading journey today — completely free."
                  : `Invest ${course.price_display} in your trading career. Lifetime access included.`
                }
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Button onClick={handleEnroll} size="lg" className="bg-[#00FF88] text-black font-bold hover:brightness-110 rounded-sm px-10 shadow-[0_0_30px_rgba(0,255,136,0.3)]" data-testid="enroll-cta-btn">
                  {enrolling ? "Processing..." : (course.is_free ? "Start Free" : `Enroll for ${course.price_display}`)} <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
                <Button variant="ghost" onClick={() => window.open(TELEGRAM_LINK, '_blank')} className="text-[#CBD5E1] hover:text-white">
                  Have Questions? Ask on Telegram
                </Button>
              </div>
            </div>
          </div>
        </Reveal>
      </section>

      {/* ═══ OUR INSTITUTE SECTION ═══ */}
      <section className="py-16 sm:py-24 px-4 sm:px-6 relative overflow-hidden" data-testid="our-institute-section">
        {/* Background ambient glows */}
        <div className="absolute top-1/2 -left-40 w-96 h-96 bg-[#00FF88]/5 rounded-full blur-[140px] pointer-events-none" />
        <div className="absolute top-1/2 -right-40 w-96 h-96 bg-[#00F0FF]/5 rounded-full blur-[140px] pointer-events-none" />

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="rounded-3xl border border-white/[0.08] bg-[#0A0D12] overflow-hidden shadow-[0_20px_60px_rgba(0,0,0,0.7)] p-6 sm:p-10 lg:p-12 relative">
            {/* Subtle background grid pattern */}
            <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:32px_32px] pointer-events-none opacity-40" />

            <div className="relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
              
              {/* Left Column: Visual Campus Showcase (7 cols) */}
              <div className="lg:col-span-7 flex flex-col gap-4">
                {/* Main Campus Photo Showcase */}
                <div className="relative h-64 sm:h-80 md:h-96 w-full rounded-2xl overflow-hidden border border-white/10 shadow-2xl bg-black/40 group">
                  <img 
                    src={selectedCampusImage} 
                    alt="Jamadhan Institute Jaipur Campus" 
                    className="w-full h-full object-cover object-center transition-all duration-500 group-hover:scale-[1.02]"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#07090D] via-transparent to-black/20" />
                  
                  {/* Top Live Badge */}
                  <div className="absolute top-4 left-4 flex items-center gap-2 px-3 py-1.5 rounded-full bg-black/60 border border-white/10 backdrop-blur-md">
                    <span className="w-2 h-2 rounded-full bg-[#00FF88] animate-pulse" />
                    <span className="text-[10px] font-mono tracking-widest text-zinc-200 uppercase font-bold">JAIPUR CAMPUS & TRADING FLOOR</span>
                  </div>

                  {/* Bottom Image Label */}
                  <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between backdrop-blur-md bg-black/60 border border-white/10 rounded-xl px-4 py-2.5">
                    <div>
                      <p className="text-xs sm:text-sm font-bold text-white font-outfit">JamaDhan Stock Market Institute</p>
                      <p className="text-[10px] sm:text-xs text-[#00FF88] font-jetbrains">Ajmer Road, Jaipur · ISO 21001:2018</p>
                    </div>
                    <span className="text-[10px] font-mono text-zinc-400 hidden sm:inline">Offline & Online</span>
                  </div>
                </div>

                {/* Interactive Campus Thumbnails Row */}
                <div className="grid grid-cols-3 gap-3">
                  {/* Thumbnail 1: Trading Floor */}
                  <button
                    type="button"
                    onClick={() => setSelectedCampusImage("/trading_floor.png")}
                    className={`rounded-xl p-2 text-left border transition-all ${
                      selectedCampusImage === "/trading_floor.png" 
                        ? "border-[#00FF88] bg-[#00FF88]/10 shadow-[0_0_15px_rgba(0,255,136,0.15)]" 
                        : "border-white/[0.08] bg-white/[0.02] hover:border-white/20 hover:bg-white/[0.04]"
                    }`}
                  >
                    <div className="h-16 sm:h-20 rounded-lg overflow-hidden relative mb-1.5 bg-black/40">
                      <img src="/trading_floor.png" alt="Trading floor" className="w-full h-full object-cover" />
                      <span className="absolute top-1 left-1 text-[7px] sm:text-[8px] font-bold font-jetbrains tracking-wider bg-[#00F0FF]/30 text-[#00F0FF] px-1 py-0.5 rounded border border-[#00F0FF]/30 backdrop-blur">
                        FLOOR
                      </span>
                    </div>
                    <p className="text-[10px] sm:text-xs font-bold font-jetbrains tracking-wider text-white truncate">TRADING FLOOR</p>
                  </button>

                  {/* Thumbnail 2: Campus Reception */}
                  <button
                    type="button"
                    onClick={() => setSelectedCampusImage("/jamadhan_reception.webp")}
                    className={`rounded-xl p-2 text-left border transition-all ${
                      selectedCampusImage === "/jamadhan_reception.webp" 
                        ? "border-[#00FF88] bg-[#00FF88]/10 shadow-[0_0_15px_rgba(0,255,136,0.15)]" 
                        : "border-white/[0.08] bg-white/[0.02] hover:border-white/20 hover:bg-white/[0.04]"
                    }`}
                  >
                    <div className="h-16 sm:h-20 rounded-lg overflow-hidden relative mb-1.5 bg-black/40">
                      <img src="/jamadhan_reception.webp" alt="Campus reception" className="w-full h-full object-cover" />
                      <span className="absolute top-1 left-1 text-[7px] sm:text-[8px] font-bold font-jetbrains tracking-wider bg-[#A855F7]/30 text-[#D8B4FE] px-1 py-0.5 rounded border border-[#A855F7]/30 backdrop-blur">
                        CAMPUS
                      </span>
                    </div>
                    <p className="text-[10px] sm:text-xs font-bold font-jetbrains tracking-wider text-white truncate">CAMPUS & LABS</p>
                  </button>

                  {/* Thumbnail 3: Link to Full Gallery */}
                  <Link
                    to="/gallery"
                    className="rounded-xl p-2 text-center border border-white/[0.08] bg-white/[0.02] hover:border-[#00FF88]/40 hover:bg-[#00FF88]/5 transition-all flex flex-col items-center justify-center group"
                  >
                    <div className="w-8 h-8 rounded-full bg-white/5 group-hover:bg-[#00FF88]/20 flex items-center justify-center transition-colors mb-1.5">
                      <ArrowRight className="w-4 h-4 text-zinc-400 group-hover:text-[#00FF88] group-hover:translate-x-0.5 transition-all" />
                    </div>
                    <p className="text-[10px] sm:text-xs font-bold font-jetbrains tracking-wider text-white uppercase">MORE GALLERY</p>
                    <span className="text-[9px] text-[#00FF88] font-jetbrains mt-0.5">15+ Photos →</span>
                  </Link>
                </div>
              </div>

              {/* Right Column: Content + 6 Features Grid (5 cols) */}
              <div className="lg:col-span-5 flex flex-col justify-center text-left">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#00FF88]/10 border border-[#00FF88]/25 text-[#00FF88] text-xs font-semibold tracking-widest uppercase font-jetbrains w-fit mb-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#00FF88] animate-pulse" />
                  Our Institute
                </div>

                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white font-outfit leading-tight mb-4">
                  Walk In. Feel The <br />
                  <span className="text-gradient-neon">Difference.</span>
                </h2>
                
                <p className="text-xs sm:text-sm text-[#CBD5E1] font-plex leading-relaxed mb-6">
                  Our institute is built for one purpose — to put you in a professional trader's environment from day one. Dedicated desks, smart boards, a live trading floor, and a community of like-minded people all working toward the same thing. Because environment shapes behaviour, and behaviour makes traders.
                </p>

                {/* 6 Features Grid */}
                <div className="grid grid-cols-2 gap-2.5 sm:gap-3 mb-6">
                  {[
                    "Online & Offline Classes",
                    "Lifetime Mentorship",
                    "ISO 21001:2018 Certified",
                    "Live Market Training",
                    "AI-Powered Drishti Tool",
                    "Limited Batch Size"
                  ].map((feature, idx) => (
                    <div key={idx} className="flex items-center gap-2.5 bg-white/[0.03] border border-white/[0.06] rounded-xl p-2.5 sm:p-3 hover:border-white/15 transition-colors">
                      <div className="flex items-center justify-center w-5 h-5 rounded-md bg-[#00FF88]/15 text-[#00FF88] shrink-0 border border-[#00FF88]/30">
                        <Check className="w-3.5 h-3.5 stroke-[3]" />
                      </div>
                      <span className="text-white font-medium text-[11px] sm:text-xs font-outfit leading-tight">{feature}</span>
                    </div>
                  ))}
                </div>

                {/* Location Pill & CTA */}
                <div className="flex flex-wrap items-center gap-3 pt-2">
                  <Link 
                    to="/gallery" 
                    className="inline-flex items-center gap-2 bg-[#00FF88] text-black font-bold hover:bg-black hover:text-white border border-transparent hover:border-[#00FF88] rounded-sm px-5 py-2.5 text-xs sm:text-sm font-outfit transition-all shadow-[0_0_15px_rgba(0,255,136,0.2)]"
                  >
                    Explore Campus Gallery <ArrowRight className="w-4 h-4" />
                  </Link>
                  <span className="text-xs text-zinc-400 font-jetbrains flex items-center gap-1.5">
                    <MapPin className="w-3.5 h-3.5 text-[#00FF88]" /> Jaipur Campus
                  </span>
                </div>
              </div>

            </div>
          </div>
        </div>
      </section>


      {/* ═══ FAQs SECTION ═══ */}
      {(meta.faqs?.length > 0 || true) && (
      <section className="py-16 sm:py-24 px-4 sm:px-6 relative" data-testid="faq-accordion-section">
        <div className="max-w-4xl mx-auto relative z-10">
          <Reveal className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-white font-outfit mb-3">Frequently Asked Questions</h2>
            <p className="text-base text-[#CBD5E1] font-plex">Everything you want to know before joining Jamadhan</p>
          </Reveal>

          <div className="space-y-4">
            {(meta.faqs?.length > 0 ? meta.faqs : [
              { q: "Do I need any prior knowledge to join?", a: "No. Our courses are built from absolute zero. Whether you've never opened a trading app or you've been losing money for years — we start from scratch and take you to a professional level step by step." },
              { q: "Are classes available online and offline both?", a: "Yes. JamaDhan offers both live online sessions and offline classroom batches in Jaipur. You can choose what suits your schedule and location best." },
              { q: "What makes JamaDhan different from other institutes?", a: "Three things — live market training during actual market hours with Deepak Sir, our AI-powered Drishti Tool that scans 500+ stocks daily, and lifetime mentorship even after your course ends. No other institute in Jaipur offers all three together." },
              { q: "How long are the courses and what is the fee?", a: "Courses range from FREE (Smart Trader Elementary) to ₹25,000 (Super Trader Combo with 35 sessions). Every course includes recordings, doubt sessions, and lifetime access to the community." },
              { q: "Will I be able to trade on my own after completing the course?", a: "Yes — that is the whole point. By the end you will have a complete trading system: entry, exit, stop loss, position sizing, and emotional discipline. Our 85% success rate speaks for itself." },
              { q: "Is there any support after the course ends?", a: "Yes. Every student gets lifetime mentorship. You can ask doubts, revisit concepts, and stay connected to the Jamadhan community forever — not just during the course." },
              { q: "Where is JamaDhan located?", a: "3rd Floor, JamaDhan Stock Market Institute, Ajmer Road, opposite MG Motors, Heera Nagar, Tagore Nagar, Jaipur, Rajasthan 302021. Also available fully online." }
            ]).map((faq, idx) => {
              const isOpen = activeFaq === idx;
              const qText = faq.q || faq.question || "";
              const aText = faq.a || faq.answer || "";
              return (
                <Reveal key={idx} delay={idx * 0.05}>
                  <div className="bg-[#0C0C10] border border-white/[0.04] hover:border-white/[0.08] rounded-xl overflow-hidden transition-all duration-300">
                    <button onClick={() => setActiveFaq(isOpen ? null : idx)} className="w-full flex items-center justify-between p-5 text-left focus:outline-none">
                      <span className="text-base sm:text-lg font-semibold text-white font-outfit pr-4">{qText}</span>
                      <ChevronDown className={`w-5 h-5 text-[#00FF88] shrink-0 transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`} />
                    </button>
                    <div className={`overflow-hidden transition-all duration-300 ${isOpen ? "max-h-[500px] opacity-100" : "max-h-0 opacity-0"}`}>
                      <p className="px-5 pb-5 text-sm sm:text-base text-[#CBD5E1] font-plex leading-relaxed border-t border-white/[0.02] pt-4">{aText}</p>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>
      )}

      {/* ═══ GOOGLE REVIEWS SECTION ═══ */}
      {(meta.reviews?.length > 0 || true) && (
      <section className="py-16 sm:py-24 px-4 sm:px-6 relative" data-testid="google-reviews-section">
        <div className="max-w-6xl mx-auto relative z-10">
          <Reveal className="text-center mb-12 flex flex-col items-center">
            <div className="flex items-center gap-2.5 bg-[#0C0C10] border border-white/[0.08] px-4 py-2 rounded-full mb-4 shadow-lg">
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z" fill="#EA4335"/></svg>
              <div className="flex gap-0.5">{[1,2,3,4,5].map((_, i) => (<Star key={i} className="w-3.5 h-3.5 text-[#FFCC00] fill-[#FFCC00]" />))}</div>
              <span className="text-xs font-bold text-white font-jetbrains">{meta.google_rating || "4.9"} ★ rated</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-white font-outfit mb-3">{meta.google_reviews_heading || "What Traders Say on Google"}</h2>
            <p className="text-base text-[#CBD5E1] font-plex">{meta.google_rating || "4.9"} ★ rated on Google · {meta.google_review_count || "90+"} verified reviews</p>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto mb-12">
            {(meta.reviews?.length > 0 ? meta.reviews : [
              { name: "Shubham Sain", role: "Student / Trader", review: "I attended JamaDhan's 7-Day Master Trader Workshop and my experience has been excellent. Deepak Sir's teaching style made everything very easy to understand. They provided recordings of all classes and conduct doubt-solving sessions. One of the best stock market institutes in Jaipur for beginners as well as serious traders." },
              { name: "Vineeta Agarwal", role: "Housewife / Investor", review: "It was an amazing and informative experience with Deepak Sir. Always available and cleared all concepts right from basics. The way of thinking totally changed after enrolling with Jama Dhan. Definitely enrolling for the next level when I visit Jaipur again." },
              { name: "Shalendra Gupta", role: "Working Professional", review: "Jama Dhan is the best institute for beginners. Limited seats in every batch ensures equal attention to every student. Deepak Sir explains every topic in detail and never moves to the next topic until all doubts are cleared. The revision habit at the start of every class is excellent." },
              { name: "Gaurav Agarwal", role: "Businessman", review: "I joined JamaDhan a month ago and it's the best institute for the share market. Super friendly environment, limited batch size so you always get personal attention, and doubts are resolved on the spot. Highly recommended." }
            ]).map((rev, idx) => (
              <Reveal key={idx} delay={idx * 0.1} className="h-full">
                <div className="bg-[#0C0C10] border border-white/[0.04] p-6 rounded-xl flex flex-col justify-between h-full transition-all duration-300 hover:border-white/[0.08]">
                  <div>
                    <div className="flex justify-between items-start mb-4">
                      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z" fill="#EA4335"/></svg>
                      <div className="flex gap-0.5">{Array.from({ length: rev.rating || 5 }).map((_, i) => (<Star key={i} className="w-3 h-3 text-[#FFCC00] fill-[#FFCC00]" />))}</div>
                    </div>
                    <p className="text-sm sm:text-base text-[#CBD5E1] font-plex leading-relaxed italic mb-6">"{rev.review}"</p>
                  </div>
                  <div className="border-t border-white/[0.04] pt-4 mt-auto">
                    <h4 className="text-sm sm:text-base font-bold text-white font-outfit">{rev.name}</h4>
                    <p className="text-xs text-[#94A3B8] font-plex mt-0.5">{rev.role}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal className="text-center" delay={0.4}>
            <a href="https://www.google.com/search?q=JamaDhan+Stock+Market+Institute+Reviews" target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#00FF88] text-black font-bold hover:bg-black hover:text-white border border-transparent hover:border-[#00FF88] transition-all duration-200 rounded-sm px-6 py-3">
              Read All Reviews on Google →
            </a>
          </Reveal>
        </div>
      </section>
      )}

      {/* ═══ OTHER COURSES ═══ */}
      {allCourses.length > 0 && (
        <section className="py-16 px-6 border-t border-white/5" data-testid="other-courses">
          <div className="max-w-7xl mx-auto">
            <Reveal>
              <p className="font-jetbrains text-xs tracking-[0.25em] uppercase text-[#CBD5E1] mb-3">EXPLORE MORE</p>
              <h2 className="text-3xl font-bold text-white font-outfit mb-8">Other Courses</h2>
            </Reveal>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {allCourses.slice(0, 3).map((c, i) => (
                <Reveal key={c.course_id} delay={i * 0.1}>
                  <Link to={`/courses/${c.course_id}`} className="tracing-card rounded-lg overflow-hidden block group" data-testid={`other-course-${i}`}>
                    {c.thumbnail && (
                      <div className="h-36 overflow-hidden relative">
                        <SeoImage src={c.thumbnail} alt={c.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#0C0C10] to-transparent" />
                      </div>
                    )}
                    <div className="p-5">
                      <h3 className="text-base font-semibold text-white font-outfit mb-1 group-hover:text-[#00FF88] transition-colors">{c.title}</h3>
                      <div className="flex items-center justify-between mt-3">
                        <span className={`text-base font-bold font-jetbrains ${c.is_free ? 'text-[#00FF88]' : 'text-white'}`}>{c.price_display}</span>
                        <span className="text-xs text-[#94A3B8] font-jetbrains">{c.sessions} sessions</span>
                      </div>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ═══ FOOTER ═══ */}
      <Footer />

      {/* ═══ ENROLLMENT REQUEST MODAL ═══ */}
      {showEnrollModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
          <div className="bg-[#0C0C10] border border-white/[0.08] rounded-xl w-full max-w-md overflow-hidden shadow-2xl">
            <div className="flex items-center justify-between p-5 border-b border-white/[0.04]">
              <h3 className="text-xl font-bold text-white font-outfit">Request Course Access</h3>
              <button onClick={() => setShowEnrollModal(false)} className="text-[#94A3B8] hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              
              {/* Automated Online Payment Option */}
              {Number(course.price || 0) > 0 && (
                <div className="p-4 rounded-xl bg-gradient-to-r from-emerald-500/15 via-cyan-500/10 to-indigo-500/10 border border-[#00FF88]/40 space-y-2.5">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-[10px] font-mono font-bold uppercase tracking-wider text-[#00FF88] block">
                        ⚡ Instant Access (Dual Stream)
                      </span>
                      <span className="text-2xl font-black font-jetbrains text-white">
                        ₹{Number(course.price).toLocaleString("en-IN")}
                      </span>
                    </div>
                    <Button
                      onClick={handleOnlineCoursePayment}
                      disabled={payingOnline}
                      className="bg-[#00FF88] text-black font-black text-xs h-10 px-5 rounded-lg shadow-md hover:brightness-110 flex items-center gap-1.5"
                    >
                      <Zap className="w-4 h-4 fill-black" />
                      <span>{payingOnline ? "Opening..." : "Pay Online Now"}</span>
                    </Button>
                  </div>
                  {/* Optional Referral Code at Checkout */}
                  <div className="pt-2 border-t border-white/10">
                    <label className="text-[10px] text-zinc-400 font-mono block mb-1">
                      Have a Referral / Partner Code? (Optional)
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={checkoutRefCode}
                        onChange={(e) => setCheckoutRefCode(e.target.value.toUpperCase())}
                        placeholder="e.g. JAM1234ABCD"
                        className="flex-1 text-xs px-3 py-1.5 bg-black/40 border border-white/10 rounded-lg text-white placeholder:text-zinc-500 focus:outline-none focus:border-[#00FF88] font-mono"
                      />
                      {checkoutRefCode && (
                        <button
                          type="button"
                          onClick={() => setCheckoutRefCode("")}
                          className="text-xs text-zinc-400 hover:text-white px-2 py-1 rounded bg-white/5 font-mono"
                        >
                          Clear
                        </button>
                      )}
                    </div>
                  </div>

                  <p className="text-[11px] text-[#94A3B8] font-plex">
                    Pay securely via UPI (GPay/PhonePe), Cards, or NetBanking. Access unlocks immediately.
                  </p>
                </div>
              )}

              <p className="text-xs text-[#CBD5E1] font-plex mb-1">
                Or request access manually from academy admin:
              </p>
              
              <div className="space-y-2">
                {[
                  { id: "trial", label: "TRIAL", desc: "Get temporary trial access to evaluate the course." },
                  { id: "paid", label: "PAID", desc: "Request full lifetime access after payment." },
                  { id: "old_student", label: "OLD STUDENT", desc: "Enrolled JamaDhan student requesting access renewal or batch extension." }
                ].map((option) => (
                  <label key={option.id} className={`flex items-start gap-3 p-4 rounded-lg border cursor-pointer transition-colors ${enrollType === option.id ? "bg-[#00FF88]/10 border-[#00FF88]/40" : "bg-white/[0.02] border-white/[0.05] hover:border-white/[0.15]"}`}>
                    <input 
                      type="radio" 
                      name="enrollType" 
                      value={option.id} 
                      checked={enrollType === option.id} 
                      onChange={() => setEnrollType(option.id)}
                      className="mt-1 sr-only"
                    />
                    <div className={`w-4 h-4 rounded-full border mt-0.5 flex items-center justify-center shrink-0 ${enrollType === option.id ? "border-[#00FF88]" : "border-[#52525B]"}`}>
                      {enrollType === option.id && <div className="w-2 h-2 rounded-full bg-[#00FF88]" />}
                    </div>
                    <div>
                      <p className={`font-semibold text-sm font-outfit ${enrollType === option.id ? "text-white" : "text-[#CBD5E1]"}`}>{option.label}</p>
                      <p className="text-xs text-[#94A3B8] font-plex mt-0.5">{option.desc}</p>
                    </div>
                  </label>
                ))}
              </div>
              
              <p className="text-xs text-[#94A3B8] font-plex text-center mt-3">
                Submitting a manual request will be reviewed by Admin Deepak.
              </p>
            </div>
            <div className="p-5 border-t border-white/[0.04] flex justify-end gap-3 bg-white/[0.01]">
              <Button variant="ghost" onClick={() => setShowEnrollModal(false)} className="text-[#A1A1AA] hover:text-white">Cancel</Button>
              <Button onClick={submitEnrollmentRequest} disabled={enrolling} className="bg-[#00FF88] text-black font-bold hover:brightness-110 shadow-[0_0_15px_rgba(0,255,136,0.2)]">
                {enrolling ? "Sending..." : "Proceed"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CourseDetailPage;
